import { ROUTES } from '@/config/routes'
import axios, { AxiosError, AxiosRequestConfig, HttpStatusCode } from 'axios'
import { clearAuthStorage, readStoredAuth } from '@/features/auth/utils/auth-storage'
import { saveSessionExpiredNotice } from '@/features/auth/utils/session-expired-notice'

export type AuthToken = string | null | undefined

export function withAuthenticatedHeaders(
  token: AuthToken,
  headers: Record<string, string> = {}
): Record<string, string> {
  const normalizedHeaders = { ...headers }

  if (token) {
    normalizedHeaders.Authorization = `Bearer ${token}`
  } else {
    delete normalizedHeaders.Authorization
  }

  return normalizedHeaders
}

export function withAuthenticatedRequest<T = unknown>(
  token: AuthToken,
  config: AxiosRequestConfig<T> = {}
): AxiosRequestConfig<T> {
  const headers = withAuthenticatedHeaders(token, config.headers as Record<string, string> | undefined)

  return {
    ...config,
    headers
  }
}

function resolveApiBaseUrl() {
  const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim()

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    const isLocalhost =
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0'

    if (isLocalhost) {
      return '/api'
    }
  }

  return configuredBaseUrl || '/api'
}

function shouldLogApiError(error: unknown) {
  if (!(error instanceof AxiosError)) {
    return true
  }

  if (error.code === 'ERR_CANCELED') {
    return false
  }

  // Alguns fluxos usam 404 como fallback entre endpoints novos e legados.
  if (error.response?.status === HttpStatusCode.NotFound) {
    return false
  }

  return true
}

function createApi() {
  const headers: Record<string, string> = {}
  const baseURL = resolveApiBaseUrl()

  const authorization = readStoredAuth()
  if (authorization?.token) {
    const token = authorization.token
    headers['Authorization'] = `Bearer ${token}`
  }

  const api = axios.create({
    baseURL,
    headers
  })

  api.interceptors.request.use(
    config => {
      const auth = readStoredAuth()
      if (auth?.token) {
        const token = auth.token
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    error => Promise.reject(error)
  )

  api.interceptors.response.use(
    response => response,
    error => {
      if (shouldLogApiError(error)) {
        console.error('API Response error:', error)
      }

      if (error instanceof AxiosError && error.response?.status === HttpStatusCode.Unauthorized) {
        const requestUrl = error.config?.url ?? ''
        const hadAuthenticatedUser = Boolean(readStoredAuth()?.token)
        const isSignInRequest =
          requestUrl.endsWith('/auth/login') ||
          requestUrl.includes('/auth/login?') ||
          requestUrl.endsWith('/auth/login-client') ||
          requestUrl.includes('/auth/login-client?')

        if (isSignInRequest) {
          return Promise.reject(error)
        }

        if (!hadAuthenticatedUser) {
          return Promise.reject(error)
        }

        saveSessionExpiredNotice()
        clearAuthStorage()
        window.location.href = ROUTES.SIGNIN.path
      }

      return Promise.reject(error)
    }
  )
  return api
}

export const publicApi = axios.create({
  baseURL: resolveApiBaseUrl()
})

const api = createApi()
export default api
